import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { PersonTargetType } from '@/types/TargetType.ts';
import { StatusTone } from '@/components/ui/statusTone.ts';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';
import { DetailsLink } from '@/features/dashboard/bookingChanges/routes/detailsLinks.ts';
import { TagWebDto } from '@/features/dashboard/tags/types/TagWebDto.ts';

/**
 * Model widoku rachunku rezerwacji.
 *
 * Warstwa pośrednia między DTO backendu (`types/dto/`) a komponentami. Ekrany
 * uczestnika, klienta i rezerwacji renderują wyłącznie te typy — dzięki temu
 * podmiana mocków na realne API dotyka tylko mapperów.
 *
 * Wiersz rachunku niesie też stan szkicu (`changed`, `overridden`, `before`),
 * bo to ta sama pozycja widziana przed zmianą i po niej; sama paczka stoi
 * w `ChangeViewModel.ts`.
 *
 * Kwoty trzymamy jako `MoneyValueDto`, nigdy jako sformatowany tekst;
 * formatowanie należy do komponentu (`MoneyAmountList`).
 *
 * **Każde pole kwotowe jest listą, po jednej pozycji na walutę.** Rezerwacja
 * bywa wielowalutowa (200 zł i 333 €) i żadne pole tego nie zwija: sumy,
 * salda i delty są listami z natury, a pozycja odwzorowująca jeden wiersz
 * backendu (rata, wpłata) niesie listę jednoelementową — żeby nie było miejsca,
 * w którym trzeba pamiętać „to akurat jest jednowalutowe”. Pusta lista znaczy
 * **brak kwoty**, nie zero; kwoty w różnych walutach nigdy się nie dodają.
 *
 * **Ta sama zasada dotyczy dat: pola `*At` i `*Date` niosą surowe ISO**
 * (`YYYY-MM-DD` albo pełny znacznik czasu), a formatuje je komponent przez
 * `utils/formatters.ts`. Formatowanie już w mapperze daje „Invalid Date”,
 * bo komponent formatuje wynik drugi raz. Wyjątki są nazwane wprost — pola
 * z przyrostkiem `Label` niosą gotowy tekst.
 */
/** Chip wielokrotnego wyboru; `added`/`removed` znaczą pozycję zmienioną w szkicu. */
export interface BillChipVm {
  label: string;
  added?: boolean;
  removed?: boolean;
}

/**
 * Akcja przycisku w wierszu — zależna od tego, co wiersz pozwala zrobić.
 * Etykiety mieszkają w `BillViewModel.utils.ts`, tak jak wszystkie
 * inne napisy enumów modułu.
 */
export enum BillRowAction {
  CHANGE = 'CHANGE',
  EDIT = 'EDIT',
  ADD = 'ADD',
  OPEN = 'OPEN',
}

/**
 * Prezentacja wiersza sprzed uwzględnienia szkicu.
 *
 * `oldValue` i `oldAmount` wystarczają, żeby w trybie edycji pokazać przekreśloną
 * poprzednią wartość, ale nie wystarczają, żeby *odtworzyć* wiersz w podglądzie:
 * pozycja dopisana szkicem miała wcześniej wyszarzone „Nie wybrano” i myślnik
 * zamiast kwoty, a tych stanów nie da się zgadnąć z samej pary wartości.
 */
export interface BillRowBeforeVm {
  value?: string;
  chips?: BillChipVm[];
  amount: MoneyValueDto[];
  amountLabel?: string;
  amountMuted?: boolean;
  muted?: boolean;
  selectedAt?: string;
}

/**
 * Pojedynczy wiersz sekcji: etykieta / wartość / kwota / akcja.
 *
 * `value` i `amount` niosą stan obowiązujący *po* uwzględnieniu szkicu;
 * `oldValue` i `oldAmount` — stan sprzed zmian. W trybie podglądu komponent
 * pokazuje wartości sprzed zmian, w trybie edycji nowe wraz z przekreśloną starą.
 */
export interface BillRowVm {
  id: string;
  label: string;
  /** Kontrolka modala edycji — wiadoma z konstrukcji wiersza, nie zgadywana. */
  type: FieldType;
  value?: string;
  chips?: BillChipVm[];
  oldValue?: string;
  /**
   * Uzasadnienie ręcznej kwoty wpisane przez operatora, tylko przy `overridden`
   * — jedyny dopisek wiersza pisany przez człowieka dla człowieka.
   */
  priceNote?: string;
  /** Pozycja ruszona w otwartym szkicu — kropka zmiany i przycisk „Edytuj”. */
  changed?: boolean;
  /** Kwota nadpisana ręcznie przez operatora — znacznik ✎. */
  overridden?: boolean;
  /** Wartość wyszarzona, np. „Nie wybrano”. */
  muted?: boolean;
  /**
   * Kiedy wybrano bieżącą wartość (tylko ubezpieczenie). Znika przy wierszu
   * zmienionym szkicem — data dotyczy wartości sprzed zmiany.
   */
  selectedAt?: string;
  /** Pusta lista = wiersz bez kwoty (pozycja niewybrana, wybór bez ceny). */
  amount: MoneyValueDto[];
  /** Kwota sprzed zmiany; pusta lista = wiersz kwoty nie zmienia. */
  oldAmount: MoneyValueDto[];
  /** Kwota wyszarzona lub zastąpiona tekstem (`amountLabel`), np. „w cenie”, „—”. */
  amountMuted?: boolean;
  /** Tekst zamiast kwoty; ma pierwszeństwo przed `amount`. */
  amountLabel?: string;
  /** Przycisk widoczny także w podglądzie (pozycje sterowane z innego ekranu). */
  locked?: boolean;
  /** Wiersz wolnej linii operatora. */
  manual?: boolean;
  /**
   * Wiersz, którego **dopisuje sam szkic** — pozycja ręczna nie ma odpowiednika
   * w rachunku, więc w stanie sprzed zmian nie istnieje. Podgląd taki wiersz
   * pomija, zamiast pokazywać pustą linijkę z myślnikiem.
   */
  draftOnly?: boolean;
  action?: BillRowAction;
  /**
   * Komplet prezentacji sprzed zmian — wypełniany tylko dla wierszy ruszonych
   * szkicem. Z niego podgląd odtwarza wiersz, zamiast wnioskować go z `oldValue`.
   */
  before?: BillRowBeforeVm;
}

/**
 * Zwijana sekcja-karta. Wspólny budulec wszystkich list w module — używana
 * identycznie na ekranie uczestnika, zapisującego i w rozwiniętym kaflu osoby.
 */
export interface BillSectionVm {
  id: string;
  title: string;
  /** Dopisek przy tytule, gdy sekcja rozwinięta (zwinięta pokazuje liczbę pozycji). */
  hint?: string;
  /** Suma sekcji per waluta; pusta lista = sekcja bez kwot. */
  sum: MoneyValueDto[];
  sumBefore: MoneyValueDto[];
  /**
   * Przypis pod siatką wierszy, widoczny przy rozwiniętej sekcji **i tylko
   * w trybie edycji** — opisuje skutek wprowadzenia zmiany („Zmiana e-maila
   * przekierowuje link akceptacyjny…”), więc w podglądzie nie ma o czym mówić.
   * Zdejmuje go `toPreviewSection`.
   */
  note?: string;
  rows: BillRowVm[];
  /**
   * Liczba wierszy sprzed filtra „Tylko zmienione”; ustawia ją `prepareSections`
   * i tylko wtedy, gdy filtr coś zabrał. Suma sekcji zależy od tego, ile
   * pozycji sekcja ma naprawdę, a nie ile z nich akurat widać.
   */
  totalRowCount?: number;
  /** Sekcja przyjmuje wolne linie — przycisk „+ Wolna linia” w trybie edycji. */
  allowAdd?: boolean;
}

/** Stopka rozliczenia pod listą sekcji (tylko ekran uczestnika). */
export interface BillTotalsVm {
  before: MoneyValueDto[];
  delta: MoneyValueDto[];
  after: MoneyValueDto[];
  /** Udział uczestnika w rezerwacji, przed i po zmianie. */
  shareBefore: BillShareVm;
  share: BillShareVm;
}

export interface BillShareVm {
  participantAmount: MoneyValueDto[];
  bookingAmount: MoneyValueDto[];
}

/** Wspólna tożsamość osoby — awatar, nazwa, opis roli. */
export interface PersonIdentityVm {
  id: string;
  kind: PersonTargetType;
  initials: string;
  fullName: string;
  /** Np. „Uczestnik · 6 lat”, „Zapisujący · płatnik”. */
  roleLabel: string;
}

/** Ekran uczestnika (A1/A2) oraz treść rozwiniętego kafla uczestnika na C1/C2. */
export interface ParticipantBillVm {
  person: PersonIdentityVm;
  sections: BillSectionVm[];
  totals: BillTotalsVm;
  /** Liczba pozycji szkicu dotyczących tego uczestnika. */
  draftItemCount: number;
}

/** Ekran osoby zapisującej (B1/B2) oraz treść rozwiniętego kafla zapisującego. */
export interface ClientBillVm {
  person: PersonIdentityVm;
  /** Dane kontaktowe + dane do faktury; bez kolumny kwot. */
  sections: BillSectionVm[];
  draftItemCount: number;
}

export interface InstallmentVm {
  id: string;
  name: string;
  dueDate: string;
  /** Rata ma jedną walutę, ale niesie ją tak jak każda inna kwota modelu. */
  amount: MoneyValueDto[];
  status: PlannedTransactionPaymentStatus;
}

/** Wpłata na rachunku. Bez statusu — transakcja finansowa to pieniądze, które już wpłynęły. */
export interface PaymentVm {
  id: string;
  paidAt: string;
  method: PaymentMethod;
  reference?: string;
  amount: MoneyValueDto[];
  tags: Pick<TagWebDto, 'name' | 'color'>[];
  /**
   * Kwota całego przelewu — tylko gdy różni się od alokacji na tę rezerwację
   * (przelew rozpisany na kilka rezerwacji albo częściowo nieprzypisany).
   */
  transferTotal?: MoneyValueDto[];
}

/** Kafel osoby na ekranie rezerwacji — zwinięty niesie kwotę, rozwinięty pełny widok. */
export interface PersonTileVm {
  person: PersonIdentityVm;
  meta: string[];
  amount: MoneyValueDto[];
  amountNote: string;
  amountMuted?: boolean;
  draftWithoutImpact?: boolean;
  /** Uczestnik po rezygnacji — kafel mówi to wprost, bo sam brak kwot nie mówi. */
  withdrawn?: boolean;
}

/** Ekran rezerwacji (C1/C2). */
export interface BookingBillVm {
  bookingId: string;
  clientId: string;
  tourId: string;
  bookingCode: string;
  tourName: string;
  dateRangeLabel: string;
  status: BookingStatus;
  client: PersonTileVm;
  participants: PersonTileVm[];
  totalValue: MoneyValueDto[];
  /** Pozycje bez przypisania do osoby — widoczne tylko w trybie edycji. */
  bookingWideSections: BillSectionVm[];
  installments: InstallmentVm[];
  payments: PaymentVm[];
}

/** Saldo rezerwacji w jednej walucie — `BalanceAmountDto` nazwane po ekranie. */
export interface BookingBalanceVm {
  totalValue: MoneyValueDto;
  paid: MoneyValueDto;
  due: MoneyValueDto;
}

/** Karta „Stan rezerwacji” w panelu bocznym (tryb podglądu). */
export interface BookingSummaryVm {
  bookingId: string;
  /** Zapisujący tej rezerwacji — klient i rezerwacja są 1:1, więc jego saldo jest saldem rezerwacji. */
  clientId: string;
  bookingCode: string;
  /** ISO-8601; utworzenie rezerwacji. */
  createdAt: string;
  tourName: string;
  dateRangeLabel: string;
  /** Po jednej pozycji na walutę; rezerwacja bez pieniędzy nie ma żadnej. */
  balances: BookingBalanceVm[];
  /** Np. „W normie”, „Zaległość”. */
  statusLabel: string;
  statusTone: StatusTone;
  /** Stan samej rezerwacji — osobny od `statusLabel`, który mówi wyłącznie o płatnościach. */
  bookingStatus: BookingStatus;
}

/** Wpis karty „Powiązane osoby”. */
export interface RelatedPersonVm {
  person: PersonIdentityVm;
  /** Dokąd prowadzi strzałka; brak = bieżąca osoba. */
  link?: DetailsLink;
  isCurrent?: boolean;
  /** Uczestnik po rezygnacji — ta sama odznaka co na kaflu obok. */
  withdrawn?: boolean;
}
